import React, { useEffect, useState } from "react";
import { Form, Space, Switch, Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useMercuryContext } from "@context/index";
import { successfulNotification } from "common/utils/notifications";
import { handleApiError } from "common/utils/handleApiError";
import { applyFormSubmitError } from "common/utils/validationErrors";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import { DEFINITION_FORM_FIELDS, DefinitionDrawer, DefinitionFormFields, DefinitionFormMode } from "stories/rules";
import { InvoiceType, invoiceTypesApi } from "./invoiceTypes.api";

export type { InvoiceType } from "./invoiceTypes.api";

type Mode = Exclude<DefinitionFormMode, "branch">;

interface InvoiceTypeFormProps {
  mode?: Mode;
  editData?: InvoiceType | null;
  refreshList?: () => void;
  disabled?: boolean;
}

interface InvoiceTypeFormValues {
  name: string;
  description?: string;
  priority: number;
  isFallback: boolean;
  active: boolean;
}

const FORM_FIELDS = [...DEFINITION_FORM_FIELDS, "isFallback"];

/**
 * Create / edit / delete / restore an invoice type. The shell and the common
 * fields are the shared rules components; what is this feature's own is the
 * single fallback type.
 */
const InvoiceTypeForm: React.FC<InvoiceTypeFormProps> = ({ mode = "create", editData, refreshList, disabled = false }) => {
  const { invoicingClient } = useMercuryContext();
  const api = invoiceTypesApi(invoicingClient);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<InvoiceTypeFormValues>();
  const [existingFallback, setExistingFallback] = useState<InvoiceType | null>(null);
  const typeId = editData?.id ?? editData?._id;

  useDrawerScrollMargin("", open);

  useEffect(() => {
    if (!open) return;

    if (editData) {
      form.setFieldsValue({ ...editData, active: editData.active ?? true });
    } else {
      form.resetFields();
      form.setFieldsValue({ active: true });
    }
  }, [open, editData, form]);

  useEffect(() => {
    const loadFallback = async () => {
      const res = await invoicingClient.post("/invoicing-v1/types/search", {
        filter: { isFallback: true, deleted: false },
      });
      setExistingFallback(res?.data?.documents?.[0] ?? null);
    };
    if (open) loadFallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setOpen(false);
    form.resetFields();
  };

  const handleSubmit = async (values: InvoiceTypeFormValues) => {
    const isDeleteRestore = (mode === "delete" || mode === "restore") && !!typeId;
    const payload = {
      ...values,
      name: values.name?.trim(),
      description: values.description?.trim(),
    };

    try {
      setLoading(true);

      if (isDeleteRestore) {
        await api.update(typeId!, { deleted: mode === "delete" });
        successfulNotification(mode === "delete" ? "Invoice type deleted" : "Invoice type restored");
      } else if (typeId) {
        await api.update(typeId, payload);
        successfulNotification("Invoice type updated");
      } else {
        await api.create({ ...payload, clientId: "google-projects", deleted: false });
        successfulNotification("Invoice type created");
      }

      refreshList?.();
      handleClose();
    } catch (error) {
      // delete/restore is driven by a confirmation Modal, not the form fields,
      // so only wire inline validation for the create/edit (form-shaped) paths.
      if (isDeleteRestore) {
        handleApiError(error, "Failed to save invoice type");
      } else {
        applyFormSubmitError(error, form, { payload, fieldNames: FORM_FIELDS, fallback: "Failed to save invoice type", noun: "invoice type" });
      }
    } finally {
      setLoading(false);
    }
  };

  const isFallbackDisabled = !!existingFallback && (mode === "create" || existingFallback._id !== typeId);

  return (
    <DefinitionDrawer
      mode={mode}
      noun="Invoice Type"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      loading={loading}
      disabled={disabled}
      width={480}
      onSubmit={() => form.submit()}
      onConfirm={() => handleSubmit({} as InvoiceTypeFormValues)}
    >
      {mode === "delete" ? (
        "Are you sure you want to delete this invoice type?"
      ) : mode === "restore" ? (
        "Are you sure you want to restore this invoice type?"
      ) : (
        <Form<InvoiceTypeFormValues> form={form} layout="vertical" onFinish={handleSubmit} initialValues={{ active: true, isFallback: false }}>
          <DefinitionFormFields
            priorityHelp="Invoice types are evaluated lowest priority first. The first whose active rules all match decides the line items."
            activeExtra="Inactive invoice types are skipped during evaluation."
          >
            <Form.Item
              name="isFallback"
              valuePropName="checked"
              label={
                <Space>
                  Fallback?
                  {existingFallback && (
                    <Tooltip
                      overlayInnerStyle={{ width: "350px" }}
                      title={
                        <div>
                          <div>Only 1 invoice type can be set as fallback.</div>
                          <div>
                            <strong>Current fallback:</strong> {existingFallback.name}
                          </div>
                        </div>
                      }
                    >
                      <InfoCircleOutlined style={{ color: "var(--primary-color)" }} />
                    </Tooltip>
                  )}
                </Space>
              }
            >
              <Switch disabled={isFallbackDisabled} />
            </Form.Item>
          </DefinitionFormFields>
        </Form>
      )}
    </DefinitionDrawer>
  );
};

export default InvoiceTypeForm;
